// Transforms the backend foundation-competency-analytics payload (per-class,
// per-competency Entry/Exit B/P/Pr counts) into the SAME shapes the Competency
// and FLN tabs already render from per-student rubric records. This lets those
// tabs show real analytics at national / region / school level, where the
// sis_model analytics payload carries no per-competency rubric.

import {
  Distribution,
  distFromRubricCounts,
  EMPTY_DIST,
  DomainCard,
  CGCard,
  IndicatorComparison,
  ExplorerDomain,
  EXPLORER_DOMAINS,
} from './competencyExplorerUtils';
import { FOUNDATIONAL_SCHEMA, CurricularGoal } from './foundationalSchema';
import { FLNClassSummary, FLNDomainSummary, LakshyaStats } from './flnUtils';
import { FLNLakshya, FLNDomain, getFLNConfig } from './flnSchema';
import {
  getClassDisplayName,
  CompetencyCountsTuple,
  ClassCompetencyAnalytics,
  FoundationCompetencyAnalyticsPayload,
} from '../../../services/holisticApi';

export type { CompetencyCountsTuple, ClassCompetencyAnalytics, FoundationCompetencyAnalyticsPayload };

type MergedCounts = Record<string, CompetencyCountsTuple>;

// ── merge per-class counts for the selected class filter ───────────────────────
export const mergeCountsForClass = (
  payload: FoundationCompetencyAnalyticsPayload | undefined,
  classFilter: number | 'all',
): { merged: MergedCounts; total: number } => {
  const merged: MergedCounts = {};
  let total = 0;
  if (!payload) return { merged, total };
  const classes = classFilter === 'all'
    ? payload.classes
    : payload.classes.filter(c => c.classId === classFilter);
  for (const c of classes) {
    total += c.total;
    for (const key of Object.keys(c.competencies)) {
      const t = c.competencies[key];
      const m = merged[key] || (merged[key] = [0, 0, 0, 0, 0, 0]);
      for (let i = 0; i < 6; i++) m[i] += t[i];
    }
  }
  return { merged, total };
};

export const hasCountsData = (merged: MergedCounts): boolean =>
  Object.keys(merged).length > 0;

// ── distribution helpers ───────────────────────────────────────────────────────
const entryDist = (t?: CompetencyCountsTuple): Distribution =>
  t ? distFromRubricCounts(t[0], t[1], t[2]) : EMPTY_DIST;
const exitDist = (t?: CompetencyCountsTuple): Distribution =>
  t ? distFromRubricCounts(t[3], t[4], t[5]) : EMPTY_DIST;

/** Pool a set of competency tuples into a single entry/exit distribution pair. */
const poolCompetencies = (merged: MergedCounts, keys: string[]): { entry: Distribution; exit: Distribution } => {
  let eb = 0, ep = 0, epr = 0, xb = 0, xp = 0, xpr = 0;
  for (const k of keys) {
    const t = merged[k];
    if (!t) continue;
    eb += t[0]; ep += t[1]; epr += t[2]; xb += t[3]; xp += t[4]; xpr += t[5];
  }
  return { entry: distFromRubricCounts(eb, ep, epr), exit: distFromRubricCounts(xb, xp, xpr) };
};

const movementOf = (entry: Distribution, exit: Distribution) =>
  +(exit.prPct - entry.prPct).toFixed(1);

// ── LEVEL 1: domain cards (pooled across the domain's CG competencies) ─────────
const cgKeysForNumbers = (cgNumbers: number[]): string[] =>
  FOUNDATIONAL_SCHEMA
    .filter(cg => cgNumbers.includes(cg.s_no))
    .flatMap(cg => cg.assessment.map(ind => ind.entryKey));

export const getDomainCardsFromCounts = (merged: MergedCounts): DomainCard[] =>
  EXPLORER_DOMAINS.map(domain => {
    const { entry, exit } = poolCompetencies(merged, cgKeysForNumbers(domain.cgNumbers));
    const current = exit.total > 0 ? exit : entry;
    return { domain, current, entry, exit, hasEntryExit: entry.total > 0 || exit.total > 0, movement: movementOf(entry, exit) };
  });

// ── LEVEL 2: CG cards within a domain ─────────────────────────────────────────
export const getCGCardsFromCounts = (merged: MergedCounts, domain: ExplorerDomain): CGCard[] =>
  FOUNDATIONAL_SCHEMA
    .filter(cg => domain.cgNumbers.includes(cg.s_no))
    .map(cg => {
      const { entry, exit } = poolCompetencies(merged, cg.assessment.map(ind => ind.entryKey));
      const current = exit.total > 0 ? exit : entry;
      return { cg, current, entry, exit, movement: movementOf(entry, exit) };
    });

// ── LEVEL 3: per-indicator Entry vs Exit ──────────────────────────────────────
export const getIndicatorComparisonsFromCounts = (merged: MergedCounts, cg: CurricularGoal): IndicatorComparison[] =>
  cg.assessment.map(indicator => {
    const t = merged[indicator.entryKey];
    const entry = entryDist(t);
    const exit = exitDist(t);
    return { indicator, entry, exit, movement: movementOf(entry, exit) };
  });

// ══════════════════════════════════════════════════════════════════════════════
//  FLN — build FLNClassSummary[] from counts (mirrors flnUtils.computeFLNClassSummary)
// ══════════════════════════════════════════════════════════════════════════════
const pct = (n: number, total: number) => (total ? +((n / total) * 100).toFixed(1) : 0);

const lakshyaStatsFromCounts = (lakshya: FLNLakshya, merged: MergedCounts): LakshyaStats => {
  const t = merged[lakshya.competencyKey] || [0, 0, 0, 0, 0, 0];
  const [eB, eP, ePr, xB, xP, xPr] = t;
  const eTotal = eB + eP + ePr;
  const xTotal = xB + xP + xPr;
  const entryProficientPct = pct(ePr, eTotal);
  const exitProficientPct = pct(xPr, xTotal);
  return {
    code: lakshya.code,
    domain: lakshya.domain,
    isHindi: lakshya.isHindi,
    shortLabel: lakshya.shortLabel,
    fullLabel: lakshya.fullLabel,
    entryBeginner: eB, entryProgressing: eP, entryProficient: ePr, entryTotal: eTotal, entryProficientPct,
    exitBeginner: xB, exitProgressing: xP, exitProficient: xPr, exitTotal: xTotal, exitProficientPct,
    movementPct: +(exitProficientPct - entryProficientPct).toFixed(1),
  };
};

const avgPct = (nums: number[]) =>
  nums.length ? +(nums.reduce((s, n) => s + n, 0) / nums.length).toFixed(1) : 0;

export const computeFLNClassSummaryFromCounts = (cls: ClassCompetencyAnalytics): FLNClassSummary | null => {
  const config = getFLNConfig(cls.classId);
  if (!config) return null;
  // Skip classes with no assessed students so the FLN tab doesn't surface an empty
  // class chip that the Competency tab (which filters total > 0) omits.
  if (cls.total <= 0) return null;

  const domainMap = new Map<FLNDomain, LakshyaStats[]>();
  config.lakshyas.forEach(lakshya => {
    const stats = lakshyaStatsFromCounts(lakshya, cls.competencies);
    if (!domainMap.has(lakshya.domain)) domainMap.set(lakshya.domain, []);
    domainMap.get(lakshya.domain)!.push(stats);
  });

  const domains: FLNDomainSummary[] = Array.from(domainMap.entries()).map(([domain, lakshyas]) => {
    const entryPct = avgPct(lakshyas.filter(l => l.entryTotal > 0).map(l => l.entryProficientPct));
    const exitPct = avgPct(lakshyas.filter(l => l.exitTotal > 0).map(l => l.exitProficientPct));
    return { domain, entryProficientPct: entryPct, exitProficientPct: exitPct, movementPct: +(exitPct - entryPct).toFixed(1), lakshyas };
  });

  const all = domains.flatMap(d => d.lakshyas);
  const overallEntry = avgPct(all.filter(l => l.entryTotal > 0).map(l => l.entryProficientPct));
  const overallExit = avgPct(all.filter(l => l.exitTotal > 0).map(l => l.exitProficientPct));

  return {
    classId: cls.classId,
    classLabel: getClassDisplayName(cls.classId), // disambiguate BV2 vs BV3 (config label is shared)
    totalStudents: cls.total,
    entryProficientPct: overallEntry,
    exitProficientPct: overallExit,
    movementPct: +(overallExit - overallEntry).toFixed(1),
    domains,
  };
};

export const computeFLNSummariesFromCounts = (
  payload: FoundationCompetencyAnalyticsPayload | undefined,
  classId?: number,
): FLNClassSummary[] => {
  if (!payload) return [];
  const source = classId !== undefined
    ? payload.classes.filter(c => c.classId === classId)
    : payload.classes;
  return source
    .map(c => computeFLNClassSummaryFromCounts(c))
    .filter((s): s is FLNClassSummary => s !== null);
};
